import { message as antMessage } from 'antd'
import React, { useState } from 'react'
import styled from 'styled-components/macro'
import { OLLIE_ACTION_COPY } from 'src/utils/ollieActionFlows'

/**
 * One draft per supplier, written from the evidence. OMS never sends them:
 * the person copies one or opens it in their own email / WhatsApp.
 */
const OllieSupplierDrafts = ({ prepared, onUsed }) => {
  const [copiedId, setCopiedId] = useState(null)
  const drafts = prepared?.drafts || []

  const handleCopy = async (draft, key) => {
    try {
      await navigator.clipboard.writeText(`${draft.subject}\n\n${draft.body}`)
      setCopiedId(key)
      onUsed('copy', draft.supplierId)
    } catch (error) {
      antMessage.error('Could not copy to clipboard')
    }
  }

  return (
    <>
      {prepared?.notice && <Notice>{prepared.notice}</Notice>}
      {drafts.map((draft, index) => {
        const key = draft.supplierId || `draft-${index}`
        return (
          <Draft key={key}>
            <Supplier>{draft.supplier || 'Supplier'}</Supplier>
            <Field>
              <FieldLabel>{OLLIE_ACTION_COPY.to}</FieldLabel>
              <span>{draft.to?.email || '—'}</span>
            </Field>
            <Field>
              <FieldLabel>{OLLIE_ACTION_COPY.subject}</FieldLabel>
              <span>{draft.subject}</span>
            </Field>
            <MessageBody>{draft.body}</MessageBody>
            {(draft.notes || []).map(note => (
              <Note key={note}>{note}</Note>
            ))}
            <Links>
              <LinkButton type='button' onClick={() => handleCopy(draft, key)}>
                {copiedId === key
                  ? OLLIE_ACTION_COPY.copied
                  : OLLIE_ACTION_COPY.copy}
              </LinkButton>
              {draft.links?.email && (
                <ExternalLink
                  href={draft.links.email}
                  onClick={() => onUsed('email', draft.supplierId)}
                >
                  {OLLIE_ACTION_COPY.openEmail}
                </ExternalLink>
              )}
              {draft.to?.whatsapp && draft.links?.whatsapp && (
                <ExternalLink
                  href={draft.links.whatsapp}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={() => onUsed('whatsapp', draft.supplierId)}
                >
                  {OLLIE_ACTION_COPY.openWhatsApp}
                </ExternalLink>
              )}
            </Links>
          </Draft>
        )
      })}
      {prepared?.message && <Note>{prepared.message}</Note>}
    </>
  )
}

const Notice = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const Draft = styled.div`
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: ${({ theme }) => theme.colours.bgGrey};
`

const Supplier = styled.p`
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Field = styled.p`
  margin: 0 0 2px;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const FieldLabel = styled.span`
  display: inline-block;
  min-width: 56px;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const MessageBody = styled.pre`
  margin: 8px 0 0;
  padding: 8px 10px;
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 6px;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Note = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 10px;
`

const LinkButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
  background: ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border-radius: 8px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`

const ExternalLink = styled.a`
  align-self: center;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.bbqBlack};
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colours.bbqBlack};
    opacity: 0.85;
  }
`

export default OllieSupplierDrafts
